/**
 * The results view state lives in the URL (mission 6 WP08 T044; FR-012,
 * FR-015; sheet 20 callouts 7 and 8, sheet 21 callout 1; research §1.18;
 * post-plan S6, C16; post-tasks P6, P20).
 *
 * Keys: `tab`, `severity`, `rule_id`, `field`, `segment`, `q` and `rule`.
 * - Every write uses `replace` and keeps the keys it does not own.
 * - An invalid value is dropped (with `replace`) and never forwarded to a
 *   wrapper (S6). An invalid `rule` is the exception: it is flagged
 *   (`ruleInvalid`) so the drawer region can say so, opens nothing, fetches
 *   nothing, and the next write drops it (P20).
 * - The shared `severity` key (P6): while `rule` is present, `severity` names
 *   the drawer's segment, and the table keeps the filter it had when the drawer
 *   opened (held here; `null` on a deep link). Closing restores it to the URL.
 *   So an open drawer never narrows, refetches or reorders the table.
 * - `drawerCloseEdit` is that close as a pure search edit (mission 8 WP09
 *   T047; TR-6, E5): the assistant hand-off passes it to `openPanel`, so the
 *   drawer's close and the panel's open are one replacing navigation.
 * - Only `severity` goes to the by-rule route. Rule, field, segment and text
 *   narrow the loaded page (`filterRows`; the spec amendment "by-rule filters
 *   beyond severity are page-local").
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { ByRuleEntry } from '@/lib/api/findings'
import { SEVERITY_ORDER, type Severity } from '@/lib/severity'

export const TABS = ['by-rule', 'by-record', 'trailer'] as const
export type ResultsTab = (typeof TABS)[number]

/** The finding page's `segment` values (mission 5 results-api.md "finding page"). */
export const SEGMENTS = ['HDR', 'BASE', 'J1', 'J2', 'K1', 'K2', 'K3', 'K4', 'L1', 'N1', 'TRL', 'logical-record'] as const

const RULE_ID = /^[A-Z0-9-]+$/
const FIELD_MAX = 64

export type PageFilterKey = 'rule_id' | 'field' | 'segment' | 'q'

export interface PageFilters {
  ruleId: string | null
  field: string | null
  segment: string | null
  q: string
}

export interface DrawerTarget {
  rule: string
  /** Null for a bare `?rule=`: every segment of the rule. */
  severity: Severity | null
}

/** The key a by-rule row is known by (focus restore, selection). */
export function rowKey(rule: string, severity: Severity): string {
  return `${rule}|${severity}`
}

function isSeverity(v: string): v is Severity {
  return (SEVERITY_ORDER as readonly string[]).includes(v)
}

function isTab(v: string): v is ResultsTab {
  return (TABS as readonly string[]).includes(v)
}

/** The keys validated on read; `q` is free text and `rule` is handled apart (P20). */
const VALID: Record<string, (v: string) => boolean> = {
  tab: isTab,
  severity: isSeverity,
  rule_id: (v) => RULE_ID.test(v),
  segment: (v) => (SEGMENTS as readonly string[]).includes(v),
  field: (v) => v.length <= FIELD_MAX,
}

function invalidKeys(params: URLSearchParams): string[] {
  return Object.entries(VALID)
    .filter(([key, ok]) => {
      const v = params.get(key)
      return v !== null && !ok(v)
    })
    .map(([key]) => key)
}

/** Rule, field, segment and text over the loaded page; text matches the rule id or description. */
export function filterRows(rows: readonly ByRuleEntry[], f: PageFilters): ByRuleEntry[] {
  const q = f.q.trim().toLowerCase()
  return rows.filter(
    (r) =>
      (f.ruleId === null || r.rule_id === f.ruleId) &&
      (f.field === null || r.field === f.field) &&
      (f.segment === null || r.segment === f.segment) &&
      (q === '' || r.rule_id.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q)),
  )
}

/**
 * The search edit that closes the drawer (TR-6, E5): `rule` and the drawer's
 * `severity` go, and the table filter `held` while it was open comes back.
 */
export function drawerCloseEdit(held: Severity | null): (params: URLSearchParams) => void {
  return (params) => {
    params.delete('rule')
    if (held === null) params.delete('severity')
    else params.set('severity', held)
  }
}

export function useResultsFilters() {
  const [params, setParams] = useSearchParams()
  const [held, setHeld] = useState<Severity | null>(null)

  const read = useMemo(() => {
    const bad = invalidKeys(params)
    const get = (key: string) => (bad.includes(key) ? null : params.get(key))
    const tabValue = get('tab')
    const sevValue = get('severity')
    const ruleValue = params.get('rule')
    const ruleValid = ruleValue !== null && RULE_ID.test(ruleValue)
    return {
      bad,
      tab: (tabValue ?? 'by-rule') as ResultsTab,
      urlSeverity: sevValue as Severity | null,
      ruleId: get('rule_id'),
      field: get('field'),
      segment: get('segment'),
      q: params.get('q') ?? '',
      rule: ruleValid ? ruleValue : null,
      ruleInvalid: ruleValue !== null && !ruleValid,
    }
  }, [params])

  // Drop invalid values (never forwarded; S6). An invalid `rule` waits for the next write (P20).
  const badKey = read.bad.join(',')
  useEffect(() => {
    if (badKey === '') return
    const next = new URLSearchParams(params)
    for (const key of badKey.split(',')) next.delete(key)
    setParams(next, { replace: true })
  }, [badKey, params, setParams])

  const write = useCallback(
    (edit: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params)
      for (const key of invalidKeys(next)) next.delete(key)
      const rule = next.get('rule')
      if (rule !== null && !RULE_ID.test(rule)) next.delete('rule')
      edit(next)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const drawer: DrawerTarget | null = read.rule === null ? null : { rule: read.rule, severity: read.urlSeverity }
  const tableSeverity = drawer ? held : read.urlSeverity
  const drawerOpen = drawer !== null

  const setTab = useCallback(
    (tab: ResultsTab) =>
      write((p) => {
        if (tab === 'by-rule') p.delete('tab')
        else p.set('tab', tab)
      }),
    [write],
  )

  const setSeverity = useCallback(
    (severity: Severity | null) => {
      if (drawerOpen) {
        setHeld(severity)
        return
      }
      write((p) => {
        if (severity === null) p.delete('severity')
        else p.set('severity', severity)
      })
    },
    [drawerOpen, write],
  )

  const setPageFilter = useCallback(
    (key: PageFilterKey, value: string | null) =>
      write((p) => {
        if (value === null || value === '') p.delete(key)
        else p.set(key, value)
      }),
    [write],
  )

  const moveDrawer = useCallback(
    (rule: string, severity: Severity | null) =>
      write((p) => {
        p.set('rule', rule)
        if (severity === null) p.delete('severity')
        else p.set('severity', severity)
      }),
    [write],
  )

  const openDrawer = useCallback(
    (rule: string, severity: Severity | null) => {
      if (!drawerOpen) setHeld(read.urlSeverity)
      moveDrawer(rule, severity)
    },
    [drawerOpen, moveDrawer, read.urlSeverity],
  )

  const closeEdit = useMemo(() => drawerCloseEdit(held), [held])
  const closeDrawer = useCallback(() => {
    write(closeEdit)
    setHeld(null)
  }, [closeEdit, write])

  return {
    tab: read.tab,
    tableSeverity,
    ruleId: read.ruleId,
    field: read.field,
    segment: read.segment,
    q: read.q,
    drawer,
    ruleInvalid: read.ruleInvalid,
    setTab,
    setSeverity,
    setPageFilter,
    openDrawer,
    moveDrawer,
    closeDrawer,
    /** `closeDrawer`'s search edit, for the assistant hand-off (TR-6). */
    drawerCloseEdit: closeEdit,
  }
}
